import React, { useEffect, useState } from 'react';
import { api, ApiError, mutate } from '../shared/api';
import { formatMoney, formatNumber, formatDateTime } from '../shared/formatting';

type Locale = 'hr' | 'en';
type Snimka = {
    id: number; izvor: string | null; created_at: string; redaka: number; temeljnica: number; duguje: number; potrazuje: number;
    prva: boolean; novih: number; obrisanih: number; promijenjenih: number; proknjizenih_nacrta: number;
};
type Tem = { godina: number; vrsta: string; broj: string; staro_d: number; staro_p: number; novo_d: number; novo_p: number; staro_n: number; novo_n: number };
type Sazetak = {
    prva_snimka?: boolean; broj_novih?: number; broj_obrisanih?: number; broj_promijenjenih?: number;
    nove?: Tem[]; obrisane?: Tem[]; promijenjene?: Tem[];
    po_kontu?: { konto: string; naziv?: string | null; saldo_delta: number }[];
    proknjizeni_nacrti?: { nacrt_id: number; opis: string; vrsta: string; broj: string }[];
};

/**
 * 10.1 Promjene uvoza: svaki uvoz ANAL_GK (dnevni feed) snima stanje po
 * temeljnici i kontu; ovdje se vidi sto je novo, obrisano ili promijenjeno u
 * odnosu na prethodni uvoz, utjecaj po kontu i koji su nacrti prepoznati kao proknjizeni.
 */
export function GlPromjeneTab({ locale, token, company }: { locale: Locale; token: string | null; company: string }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [snimke, setSnimke] = useState<Snimka[]>([]);
    const [selId, setSelId] = useState<number | null>(null);
    const [detail, setDetail] = useState<Sazetak | null>(null);
    const [lista, setLista] = useState<'nove' | 'promijenjene' | 'obrisane' | 'po_kontu'>('po_kontu');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadList = () => api<{ snimke: Snimka[] }>(`/gl-analitika/promjene?company=${company}`, token)
        .then((r) => { setSnimke(r.snimke); setSelId((cur) => cur ?? r.snimke[0]?.id ?? null); })
        .catch((e) => setError(e instanceof ApiError ? e.message : 'API'));

    useEffect(() => { setSelId(null); setDetail(null); loadList(); }, [company, token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selId === null) return;
        api<{ sazetak: Sazetak }>(`/gl-analitika/promjene/${selId}`, token)
            .then((r) => setDetail(r.sazetak))
            .catch((e) => setError(e instanceof ApiError ? e.message : 'API'));
    }, [selId, token]);

    async function snimi() {
        setBusy(true);
        setError(null);
        try {
            const r = await mutate<{ id: number }>(`/gl-analitika/promjene/snimka?company=${company}`, token, 'POST', {});
            setSelId(r.id);
            await loadList();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'API');
        } finally {
            setBusy(false);
        }
    }

    const m = (v: number) => formatMoney(v, locale);
    const num: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' };
    const tems = lista === 'po_kontu' ? [] : (detail?.[lista] ?? []);

    return (
        <div>
            <p className="muted" style={{ marginTop: 0 }}>
                {t('Svaki uvoz glavne knjige sam napravi snimku i usporedi je s prethodnom. Nacrt čija se konta i iznosi pojave u novoj temeljnici prelazi u Proknjiženo.',
                    'Every ledger import takes a snapshot and compares it with the previous one. A draft whose accounts and amounts appear in a new journal is marked as booked.')}
            </p>
            <button type="button" className="secondary compact" onClick={snimi} disabled={busy}>{busy ? t('Snimam…', 'Saving…') : t('Snimi stanje sada', 'Snapshot now')}</button>
            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            <div style={{ overflow: 'auto', maxHeight: 260, margin: '0.8rem 0' }}>
                <table className="data-table compact">
                    <thead><tr>
                        <th>#</th><th>{t('Vrijeme', 'Time')}</th><th>{t('Izvor', 'Source')}</th><th style={num}>{t('Redaka', 'Rows')}</th><th style={num}>{t('Temeljnica', 'Journals')}</th>
                        <th style={num}>{t('Novih', 'New')}</th><th style={num}>{t('Promijenjenih', 'Changed')}</th><th style={num}>{t('Obrisanih', 'Deleted')}</th><th style={num}>{t('Proknjiženih nacrta', 'Booked drafts')}</th>
                    </tr></thead>
                    <tbody>
                        {snimke.map((s) => (
                            <tr key={s.id} onClick={() => setSelId(s.id)} style={{ cursor: 'pointer', fontWeight: s.id === selId ? 700 : 400 }}>
                                <td>{s.id}</td><td>{formatDateTime(s.created_at, locale)}</td><td>{s.izvor ?? ''}</td>
                                <td style={num}>{formatNumber(s.redaka, locale)}</td><td style={num}>{formatNumber(s.temeljnica, locale)}</td>
                                {s.prva ? <td colSpan={4} className="muted">{t('prva snimka, nema usporedbe', 'first snapshot, nothing to compare')}</td> : (
                                    <><td style={num}>{s.novih}</td><td style={num}>{s.promijenjenih}</td><td style={num}>{s.obrisanih}</td><td style={num}>{s.proknjizenih_nacrta}</td></>
                                )}
                            </tr>
                        ))}
                        {snimke.length === 0 ? <tr><td colSpan={9} className="muted">{t('Još nema snimki. Prva nastaje sljedećim uvozom ili gumbom iznad.', 'No snapshots yet. The first comes with the next import or the button above.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {detail && !detail.prva_snimka ? (
                <>
                    {detail.proknjizeni_nacrti?.length ? (
                        <p><strong>{t('Prepoznati kao proknjiženi', 'Recognised as booked')}:</strong>{' '}
                            {detail.proknjizeni_nacrti.map((n) => `#${n.nacrt_id} ${n.opis} = ${n.vrsta}/${n.broj}`).join('; ')}</p>
                    ) : null}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        {([['po_kontu', t('Utjecaj po kontu', 'Impact per account')], ['nove', `${t('Nove', 'New')} (${detail.broj_novih ?? 0})`],
                            ['promijenjene', `${t('Promijenjene', 'Changed')} (${detail.broj_promijenjenih ?? 0})`], ['obrisane', `${t('Obrisane', 'Deleted')} (${detail.broj_obrisanih ?? 0})`]] as const)
                            .map(([k, l]) => <button key={k} type="button" className={lista === k ? 'compact' : 'secondary compact'} onClick={() => setLista(k)}>{l}</button>)}
                    </div>
                    {lista === 'po_kontu' ? (
                        <table className="data-table compact">
                            <thead><tr><th>{t('Konto', 'Account')}</th><th>{t('Naziv', 'Name')}</th><th style={num}>{t('Promjena salda (D-P)', 'Balance change (D-C)')}</th></tr></thead>
                            <tbody>
                                {(detail.po_kontu ?? []).map((k) => <tr key={k.konto}><td>{k.konto}</td><td>{k.naziv ?? ''}</td><td style={num}>{m(k.saldo_delta)}</td></tr>)}
                                {(detail.po_kontu ?? []).length === 0 ? <tr><td colSpan={3} className="muted">{t('Salda konta nisu promijenjena.', 'No balance changed.')}</td></tr> : null}
                            </tbody>
                        </table>
                    ) : (
                        <table className="data-table compact">
                            <thead><tr>
                                <th>{t('Godina', 'Year')}</th><th>{t('Vrsta', 'Type')}</th><th>{t('Broj', 'No.')}</th>
                                <th style={num}>{t('Prije D', 'Before D')}</th><th style={num}>{t('Prije P', 'Before C')}</th>
                                <th style={num}>{t('Sada D', 'Now D')}</th><th style={num}>{t('Sada P', 'Now C')}</th><th style={num}>{t('Stavki prije / sada', 'Lines before / now')}</th>
                            </tr></thead>
                            <tbody>
                                {tems.map((r) => (
                                    <tr key={`${r.godina}-${r.vrsta}-${r.broj}`}>
                                        <td>{r.godina}</td><td>{r.vrsta}</td><td>{r.broj}</td>
                                        <td style={num}>{m(r.staro_d)}</td><td style={num}>{m(r.staro_p)}</td><td style={num}>{m(r.novo_d)}</td><td style={num}>{m(r.novo_p)}</td>
                                        <td style={num}>{r.staro_n} / {r.novo_n}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            ) : null}
        </div>
    );
}
